import React from 'react';

const DesignInformation = ({ data, updateData }) => {
  const handleCeilingTypeSelection = (ceilingId) => {
    const newCeiling = data.ceilingType === ceilingId ? '' : ceilingId;
    updateData({ ceilingType: newCeiling });

    // Show/hide material sections based on ceiling type
    if (newCeiling === 'ceiling_painted_slab') {
      // For painted slab, we need paint only
      updateData({
        materialType: '',
        materialSpecs: { ...data.materialSpecs, paintOnly: true }
      });
    } else {
      // For other ceiling types, we need framing and boards
      updateData({
        materialSpecs: { ...data.materialSpecs, paintOnly: false }
      });
    }
  };

  const handleAccentSelection = (accentId) => {
    const newAccent = data.accentConfig === accentId ? '' : accentId;
    updateData({
      accentConfig: newAccent,
      accentSubtype: newAccent !== accentId ? '' : data.accentSubtype
    });

    // Show/hide subtype options
    if (newAccent === 'accent_recessed_cove' || newAccent === 'accent_tray') {
      // These types have subtypes
      updateData({ showSubtypes: true });
    } else {
      updateData({ showSubtypes: false });
    }
  };

  const handleSubtypeSelection = (subtypeId) => {
    const newSubtype = data.accentSubtype === subtypeId ? '' : subtypeId;
    updateData({ accentSubtype: newSubtype });
  };

  const ceilingTypes = [
    {
      id: 'ceiling_painted_slab',
      title: 'Painted Exposed Slab Soffit',
      description: 'The structural slab underside is left visible and painted for finish and protection.'
    },
    {
      id: 'ceiling_flat',
      title: 'Flat Ceilings',
      description: 'A level ceiling fixed directly under the slab or framing to create a continuous surface.'
    },
    {
      id: 'ceiling_suspended',
      title: 'Flat Suspended',
      description: 'A level ceiling hung below the structure with space above for services and access.'
    }
  ];

  const accentConfigs = [
    {
      id: 'accent_shadow_gap',
      title: 'AC01: Shadow Gap',
      description: 'Narrow recessed line detail at ceiling perimeter.'
    },
    {
      id: 'accent_curtain_gap',
      title: 'AC02: Curtain Gap',
      description: 'Linear recess near wall for concealed curtain tracks.'
    },
    {
      id: 'accent_recessed_cove',
      title: 'AC03: Recessed Cove',
      description: 'Perimeter recess with hidden lighting for indirect illumination.',
      subtypes: [
        {
          id: 'ac03_01_single_linear',
          title: 'AC03-01: Single Linear',
          description: 'Single recessed or raised linear feature in ceiling plane.'
        },
        {
          id: 'ac03_02_double_linear',
          title: 'AC03-02: Double Linear',
          description: 'Two parallel recessed or raised linear features in ceiling plane.'
        },
        {
          id: 'ac03_03_perimeter',
          title: 'AC03-03: Perimeter',
          description: 'Accent running continuously around the ceiling\'s perimeter.'
        },
        {
          id: 'ac03_04_centered',
          title: 'AC03-04: Centered',
          description: 'Accent or feature positioned centrally in the ceiling.'
        }
      ]
    },
    {
      id: 'accent_tray',
      title: 'AC04: Tray',
      description: 'Raised or recessed ceiling section creating depth and visual interest.',
      subtypes: [
        {
          id: 'ac04_01_plain',
          title: 'AC04-01: Plain',
          description: 'Simple tray ceiling with no decorative accent or recess.'
        },
        {
          id: 'ac04_02_cove',
          title: 'AC04-02: Cove',
          description: 'Tray with cove transition and hidden lighting capability.'
        }
      ]
    }
  ];

  return (
    <div className="card p-3">
      <h3 className="font-semibold mb-3">2.3. DESIGN INFORMATION</h3>
      
      <div className="mb-4">
        <h4 className="font-medium mb-3">CEILING TYPE (Choose 1 Ceiling type)</h4>
        <div className="grid gap-3">
          {ceilingTypes.map(ceiling => (
            <label
              key={ceiling.id}
              className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="radio"
                name="ceiling_type"
                checked={data.ceilingType === ceiling.id}
                onChange={() => handleCeilingTypeSelection(ceiling.id)}
                className="mt-1"
              />
              <div>
                <div className="font-semibold">{ceiling.title}</div>
                <div className="text-sm text-gray-600 mt-1">{ceiling.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <h4 className="font-medium mb-3">ACCENT CONFIGURATION (Choose 1 Configuration)</h4>
        <div className="grid gap-3">
          {accentConfigs.map(accent => (
            <div key={accent.id}>
              <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="accent_config"
                  checked={data.accentConfig === accent.id}
                  onChange={() => handleAccentSelection(accent.id)}
                  className="mt-1"
                />
                <div>
                  <div className="font-semibold">{accent.title}</div>
                  <div className="text-sm text-gray-600 mt-1">{accent.description}</div>
                </div>
              </label>

              {accent.subtypes && data.accentConfig === accent.id && (
                <div className="ml-8 grid gap-2 mt-2">
                  {accent.subtypes.map(subtype => (
                    <label
                      key={subtype.id}
                      className="flex items-start gap-3 p-2 border border-gray-100 rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name={accent.id === 'accent_recessed_cove' ? 'recessed_cove_subtype' : 'tray_subtype'}
                        checked={data.accentSubtype === subtype.id}
                        onChange={() => handleSubtypeSelection(subtype.id)}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-semibold">{subtype.title}</div>
                        <div className="text-sm text-gray-600 mt-1">{subtype.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DesignInformation;